/**
 * Pure helpers for the request-log rows (`RequestLogTable`), kept out of
 * the component file so the detail sheet can share them and the row's
 * open-on-click behaviour is testable without a DOM.
 */

import type { KeyboardEvent, MouseEvent } from 'react'

export function formatTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

/**
 * A 2xx is the one status worth reading as GOOD, so it gets the same green
 * the Live serving badge uses. Grey said "nothing to see here" for the row
 * you most want to confirm at a glance.
 */
export function statusVariant(
  status: number,
): 'success' | 'secondary' | 'destructive' | 'outline' {
  if (status >= 500) return 'destructive'
  if (status >= 400) return 'outline'
  if (status >= 200 && status < 300) return 'success'
  return 'secondary'
}

/**
 * Derive the trace id from an R2 trace key. The gateway keys traces as
 * `traces/{team}/{YYYY}/{MM}/{DD}/[{conversationId}/]{traceId}.json`.
 */
export function traceIdFromKey(traceKey: string): string {
  const file = traceKey.split('/').pop() ?? traceKey
  return file.replace(/\.json$/, '')
}

/** Did the event start inside a link, such as the Model cell's? */
function insideLink(target: EventTarget | null): boolean {
  if (target === null || !('closest' in target)) return false
  return (target as Element).closest('a') !== null
}

/** The row-level props that open a detail sheet. */
export interface RowOpenProps {
  tabIndex: 0
  'aria-label': string
  onClick: (event: Pick<MouseEvent, 'target'>) => void
  onKeyDown: (event: Pick<KeyboardEvent, 'key' | 'preventDefault' | 'target' | 'currentTarget'>) => void
}

/**
 * Props that make a row open its trace or conversation by `key`, or
 * undefined for a row that has none to open. `label` is the row's
 * aria-label, naming what opens. A click that started on a link inside
 * the row (the Model cell's) navigates there and must not also open the
 * sheet. A key press bubbles up from a focused child the same way, so
 * Enter on the link navigates without opening the sheet and Space is left
 * to the child: only the focused row itself opens.
 */
export function rowOpenProps(
  key: string | null | undefined,
  onOpen: ((key: string) => void) | undefined,
  label: string,
): RowOpenProps | undefined {
  if (!key || onOpen === undefined) return undefined
  return {
    tabIndex: 0,
    'aria-label': label,
    onClick: (event) => {
      if (insideLink(event.target)) return
      onOpen(key)
    },
    onKeyDown: (event) => {
      if (event.target !== event.currentTarget) return
      if (event.key !== 'Enter' && event.key !== ' ') return
      event.preventDefault()
      onOpen(key)
    },
  }
}
